import { useEffect, useState } from "react";
import { fetcher } from "../../../services/api";
import AlertIcon from "../../../assets/icons/alert";
import ArrowUpRightIcon from "../../../assets/icons/arrow-up-right";
import CheckIcon from "../../../assets/icons/check";
import DocumentIcon from "../../../assets/icons/document";
import AppButton from "../../AppButton";
import AppModal from "../../AppModal";
import AppModalContent from "../../AppModal/AppModalContent";
import AppModalHeader from "../../AppModal/AppModalHeader";
import Loader from "../../Loader/Loader";
import "./AdsLinesModal.css";

interface AdsLinesModalProps {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  clientKey: string;
  onUpdateAppIsValidInListing?: (key: string, ad_lines_is_valid: boolean) => void;
}
export default function AdsLinesModal({
  isVisible,
  setIsVisible,
  clientKey,
  onUpdateAppIsValidInListing,
}: AdsLinesModalProps) {
  const [adsLines, setAdsLines] = useState<any>();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adNetworkLines, setadNetworkLines] = useState<any>();
  const [lastCheckedTime, setLastCheckedTime] = useState<string | null>(null);

  const getAdNetworkLines = async (key: string) => {
    try {
      const res = await fetcher(`/publisher/client/${key}/ad-network-lines`, {
        method: "GET",
      });
      setAdsLines((res.data as { data: any }).data);
      setLastCheckedTime(
        (res.data as { data: any }).data.ad_lines_last_checked_date
      );
    } catch (err) {
      console.error("Failed to get ad network lines:", err);
    }
  };

  const checkAdNetworkLines = async (key: string) => {
    try {
      setLoading(true);
      const res = await fetcher(
        `/publisher/client/${key}/check/ad-network-lines`,
        {
          method: "POST",
        }
      );
      const data = res.data as { data: any };
      if (data) {
        setAdsLines(data.data);
        setLastCheckedTime(data.data.ad_lines_last_checked_date);
        if (onUpdateAppIsValidInListing) {
          onUpdateAppIsValidInListing(key, data.data.ad_lines_is_valid)
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to check ad network lines:", err);
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (adsLines?.lines.length && navigator?.clipboard?.writeText) {
      const lines = ['# KLIPY Start', ...adsLines.lines, '# KLIPY End']
      navigator.clipboard
        .writeText(lines.join("\n"))
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
        });
    } else {
      console.warn("Clipboard API not supported.");
    }
  };

  useEffect(() => {
    if (isVisible && clientKey) {
      getAdNetworkLines(clientKey);
      setLoading(false);
      setadNetworkLines("");
      setAdsLines("");
      setLastCheckedTime("");
    }
  }, [isVisible, clientKey]);

  return (
    <AppModal
      isVisible={isVisible}
      onChange={() => {
        setIsVisible(!isVisible);
      }}
      className="ads-lines-modal"
    >
      <AppModalHeader
        onClose={() => {
          setIsVisible(!isVisible);
        }}
      >
        <DocumentIcon />
        KLIPY ADS Lines
        {lastCheckedTime && (
          <div className="ads-last-updated">
            Last updated on:{" "}
            {new Date(lastCheckedTime).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })}
          </div>
        )}
      </AppModalHeader>
      <AppModalContent>
        <div className="ads-lines-content">
          <div className="ads-lines-content-left">
            <div className="ads-lines-content-left-texts">
              <div className="ads-line-item"># KLIPY Start</div>
              {adNetworkLines &&
                adNetworkLines.lines &&
                adNetworkLines?.lines.map((line: string, index: number) => {
                  return (
                    <div key={index} className="ads-line-item">
                      {line}
                    </div>
                  );
                })}
              {adsLines &&
                !adNetworkLines &&
                adsLines.lines.map((line: string, index: number) => {
                  return (
                    <div key={index} className="ads-line-item">
                      {line}
                    </div>
                  );
                })}
              <div className="ads-line-item"># KLIPY End</div>
            </div>
            <div className="ads-lines-content-left-bottom">
              <div className="ads-lines-content-left-validation">
                {!loading && adsLines?.ad_lines_is_valid ? (
                  <div className="ads-lines-content-left-valid">
                    <CheckIcon />
                    <span>The app-ads.txt file is up to date.</span>
                  </div>
                ) : adsLines?.lines?.length > 0 &&
                  adsLines?.ad_lines_is_valid === false &&
                  !adNetworkLines?.message ? (
                  <>
                    <div className="ads-lines-content-left-invalid">
                      <AlertIcon />
                      <span>The app-ads.txt file needs an update.</span>
                    </div>
                    <span className="ads-lines-message-text">
                      {adsLines.lines_diff.length} {adsLines.lines_diff.length > 1 ? 'lines are' : 'line is'} missing from your current app-ads.txt file. Please copy the entire list below and replace your existing ones. Afterwards, click the "Check Again" button to complete the validation.
                    </span>
                  </>
                ) : null}
                {adNetworkLines && adNetworkLines?.message && (
                  <div className="ads-lines-content-left-invalid">
                    <AlertIcon />
                    <span>{adNetworkLines?.message}</span>
                  </div>
                )}
              </div>
              <div className="ads-lines-content-left-actions">
                <AppButton
                  variant="yellow"
                  fullWidth
                  onClick={() => checkAdNetworkLines(clientKey)}
                >
                  {loading ? <Loader /> : "Check Again"}
                </AppButton>
                <AppButton variant="secondary" fullWidth onClick={handleCopy}>
                  {copied ? "Copied" : "Copy"}
                </AppButton>
              </div>
            </div>
          </div>
          <div className="ads-lines-content-right">
            <article>
              <a href="faqs#3" target="_blank">
                <div className="ads-lines-content-right-title">
                  Why is app-ads.txt required for monetization?
                </div>
                <div className="ads-lines-content-right-actions">
                  <p>View Details</p>
                  <span>
                    <ArrowUpRightIcon />
                  </span>
                </div>
              </a>
            </article>
            <article>
              <a href="faqs#4" target="_blank">
                <div className="ads-lines-content-right-title">
                  How to resolve “Update Required” status?
                </div>
                <div className="ads-lines-content-right-actions">
                  <p>View Details</p>
                  <span>
                    <ArrowUpRightIcon />
                  </span>
                </div>
              </a>
            </article>
          </div>
        </div>
      </AppModalContent>
    </AppModal>
  );
}
